import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Bounce, ToastContainer, toast } from 'react-toastify';
import Header from '~/componentsPage/Header/Header';
import styles from '~/styles/PageNotFound/PageNotFound.module.css';

type Status = 'form' | 'success';

function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('form');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error('Brak tokena resetu hasła w adresie URL.', { position: 'top-center' });
      return;
    }

    if (password.length < 4) {
      toast.warning('Hasło musi mieć co najmniej 4 znaki.', { position: 'top-center' });
      return;
    }

    if (password !== confirmPassword) {
      toast.warning('Hasła nie są identyczne.', { position: 'top-center' });
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();

      if (res.ok && data.status === 200) {
        toast.success(data.message || 'Hasło zostało zmienione!', {
          position: 'top-center',
          autoClose: 6000,
          transition: Bounce,
        });
        setStatus('success');
        setTimeout(() => navigate('/'), 4000);
      } else {
        toast.error(data.message || 'Nie udało się zresetować hasła.', { position: 'top-center' });
      }
    } catch {
      toast.error('Wystąpił błąd połączenia z serwerem. Spróbuj ponownie później.', { position: 'top-center' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid var(--border-color, #d0d0d0)',
    background: 'var(--bg-input, #fff)',
    color: 'var(--text-main, #333)',
    fontSize: '1rem',
    boxSizing: 'border-box',
  };

  return (
    <div className={styles.pageContainer}>
      <ToastContainer />
      <Header />

      <main className={styles.contactContent}>
        <div
          className={styles.contactCard}
          style={{ padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}
        >
          {status === 'form' && (
            <>
              <div style={{ fontSize: '3rem' }}>🔑</div>
              <h1 style={{ fontSize: '1.8rem', margin: 0 }}>Ustaw nowe hasło</h1>
              <p style={{ color: 'var(--text-muted)', margin: 0, textAlign: 'center', maxWidth: '400px' }}>
                Wpisz nowe hasło do swojego konta. Po zmianie hasła zostaniesz wylogowany ze wszystkich urządzeń.
              </p>

              <form
                onSubmit={handleSubmit}
                style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%', maxWidth: '360px', marginTop: '10px' }}
                noValidate
              >
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value.replace(/\s/g, ''))}
                  placeholder="Nowe hasło (min. 4 znaki)"
                  style={inputStyle}
                  autoComplete="new-password"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value.replace(/\s/g, ''))}
                  placeholder="Powtórz nowe hasło"
                  style={inputStyle}
                  autoComplete="new-password"
                />

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', cursor: 'pointer', color: 'var(--text-main)' }}>
                  <input
                    type="checkbox"
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                  />
                  Pokaż hasło
                </label>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={styles.socialBtn}
                  style={{ background: 'var(--accent-color)', color: 'white', padding: '14px', fontSize: '1.05rem', border: 'none', cursor: isSubmitting ? 'default' : 'pointer', opacity: isSubmitting ? 0.7 : 1 }}
                >
                  {isSubmitting ? 'Zapisywanie...' : 'Zmień hasło'}
                </button>
              </form>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ fontSize: '4rem' }}>✅</div>
              <h1 style={{ fontSize: '2rem', margin: 0, color: 'var(--accent-color)' }}>Hasło zmienione!</h1>
              <p style={{ maxWidth: '400px', margin: '0 auto', color: 'var(--text-muted)', textAlign: 'center' }}>
                Twoje hasło zostało pomyślnie zmienione. Za chwilę przeniesiemy Cię na stronę główną, gdzie możesz się zalogować nowym hasłem.
              </p>
              <Link
                to="/"
                className={styles.socialBtn}
                style={{ background: 'var(--accent-color)', color: 'white', padding: '15px 40px', fontSize: '1.1rem', marginTop: '10px' }}
              >
                Przejdź do logowania
              </Link>
            </>
          )}
        </div>
      </main>

      <footer className={styles.mainFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerLinks}>
            <Link to="/pomoc">Pomoc</Link>
            <Link to="/kontakt">Kontakt</Link>
            <Link to="/polityka-prywatnosci">Polityka prywatności</Link>
            <Link to="/regulamin">Regulamin</Link>
          </div>
          <div className={styles.copyright}>© 2026 Czateria24. Wszystkie prawa zastrzeżone.</div>
        </div>
      </footer>
    </div>
  );
}

export default ResetPassword;
